import * as React from "react";
import { useLocalStorage } from "@gravitywelluk/react-hooks";
import { useState } from "react";
import _ from "lodash";
import { v4 as uuid } from "uuid";
import styled, { css } from "styled-components";
import { toast } from "react-toastify";
import { useMappedState } from "redux-react-hook";

import { breakpoint } from "src/library/utils/styled-media-queries";
import Grid from "src/library/components/Hedron";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { CheckboxStyled } from "src/library/components/Forms/Checkbox";
import { Button } from "src/library/components/Button";
import { Text } from "src/library/components/Text";
import {
  ISupportTicket,
  CreateSupportTicketParams,
  CreditStatus,
  UpdateSupportTicketParams
} from "src/modules/support/api/support.api";
import useOrder from "src/core/orders/hooks/useOrder";
import useCurrentUser from "src/core/users/hooks/useCurrentUser";
import {
  IOrderLine,
  IOrder
} from "src/core/orders/api/orders.api";
import { IStore } from "src/core/shared/store";
import DropZoneMultiFile from "src/core/shared/components/dropzones/DropZoneMultiFile";
import { FileData } from "src/core/shared/components/files/ListFiles";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";

import { CreditRequestOrderLine } from "../components/CreditRequestOrderLine";
import { CreditOrderLineSelection } from "../components/CreditOrderLineSelection";
import { CreditRequestStatusBar } from "../components/CreditRequestStatusBar";
import { validateCreditRow } from "../utils/validate-credit-row";
import { CreditRowHeadersRight } from "../components/CreditRowHeaderRight";
import {
  CreditRequestStatus,
  ICreditRequest,
  useCreditRequestApi
} from "../api/credit-request.api";
import { CloseTicketWithCreditsModel } from "../components/CloseTicketWithCreditsModel";

export type CreditRequestEditMode = "edit" | "review" | "view";

// ------------ Context typings ---------- //
interface ICreditRequestContextState {
  isLoading: boolean;
  credits: CreditRequestRowType[];
  mode: CreditRequestEditMode;
  supportTicket: Partial<ISupportTicket> | null;
  order: IOrder | null;
  hasChanged: boolean;
  approveAll: boolean;
}
interface ICreditRequestContextActions {
  setCreditRequestRow: (credit: CreditRequestRowType, orderLine: IOrderLine) => void;
  saveCredits: () => void;
  removeCreditRow: (credit: CreditRequestRowType) => void;
}

interface ICreditRequestContext {
  state: ICreditRequestContextState;
  actions: ICreditRequestContextActions
}

// ------------ end Context typings ---------- //

export type CreditRequestRowType = {
  // Local identifier for efficiency when performing updates
  localId?: string;
  error?: string | null;
} &
  Pick<ICreditRequest, "orderLineId" | "quantityRequested" | "creditRequestReasonCodeId"> &
  Partial<Pick<ICreditRequest, "exported" | "status" | "reviewedByUserId" | "id" | "supportTicketId" | "quantityApproved">>;

// State that will get set to local storage
type PersistedCreditState = Pick<ICreditRequestContextState, "credits" | "mode"> & {ticketId?: number};

/**
 * Helper hook function for using context state
 */
export const useCreditRequestContext = (): [ICreditRequestContextState, ICreditRequestContextActions] => {
  const { actions, state } = React.useContext(CreditRequestContext);

  return [ state, actions ];
};

// Provide props
interface CreditRequestContextProviderProps {
  supportTicket: Partial<ISupportTicket>;
  saveSupportTicket: (update: Pick<UpdateSupportTicketParams, "credits" | "submitReview"> | Pick<CreateSupportTicketParams, "credits" | "evidence">) => void;
  onCancelCreditRequest?: () => void;
}

// initial context setup
const initialContext: ICreditRequestContext = {
  state: {
    credits: [],
    isLoading: true,
    mode: "view",
    supportTicket: null,
    order: null,
    hasChanged: false,
    approveAll: false
  },
  actions: {
    setCreditRequestRow: (_credit: CreditRequestRowType) => null,
    saveCredits: () => null,
    removeCreditRow: (_credit: CreditRequestRowType) => null
  }
};

const CreditRequestContext = React.createContext<ICreditRequestContext>(initialContext);

export interface CreditRequestContextProviderRef {
  setSupportTicket: (ticket: ISupportTicket | null) => void;
  setMode: (mode: CreditRequestEditMode) => void;
  setShowCloseTicketWithCreditsModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const CreditRequestContextProvider: React.ForwardRefRenderFunction<CreditRequestContextProviderRef, CreditRequestContextProviderProps> = (props, ref) => {
  // persisted state object
  const [ creditStorage, setCreditStorage ] = useLocalStorage<PersistedCreditState>("credits:ticket", {
    ...initialContext.state,
    ticketId: props.supportTicket?.id
  });

  const [ getCreditsByOrderRes, getCreditsByOrderReq ] = useCreditRequestApi("GET_CREDIT_REQUESTS_BY_ORDER");

  // Main state object
  const [
    {
      supportTicket, credits, mode, isLoading, order, hasChanged, approveAll
    },
    setCreditState
  ] = useState<ICreditRequestContextState>({
    ...initialContext.state,
    supportTicket: props.supportTicket,
    mode: "edit"
  });

  const [ hasLoadedStorage, setHasLoadedStorage ] = useState(false);
  const [ { currentOrder } ] = useOrder(supportTicket?.orderId, null, true);
  const [ evidence, setEvidence ] = useState<FileData[]>([]);
  const [ showCloseTicketWithCreditsModal, setShowCloseTicketWithCreditsModal ] = useState(false);
  const ticketTypes = useMappedState((store: IStore) => (store.global.settings?.supportTicketTypes));
  const [ { currentRole } ] = useCurrentUser();
  const ticketType = React.useMemo(() => _.find(ticketTypes, type => type.id === props.supportTicket?.supportTicketTypeId), [ props.supportTicket?.supportTicketTypeId, ticketTypes ]);

  /** boolean of if the credit has already been exported and should therefore be locked */
  const hasExportedCreditForTicket = React.useMemo(() => {
    if (!supportTicket?.id) {
      return false;
    }

    return _.find(credits, cred => cred.exported && supportTicket.id === cred.supportTicketId);
  }, [ credits, supportTicket?.id ]);

  /** Boolean of if the credit requires evidence for it to be submitted */
  const requireEvidence = React.useMemo(() => {
    // allow admins to skip evidence
    if (currentRole?.type === "admin") {
      return false;
    }

    return typeof ticketType?.options.allowCredits === "object" ? ticketType?.options.allowCredits.requireEvidence : false;
  }, [ currentRole?.type, ticketType?.options.allowCredits ]);

  /** Boolean to decide if the approval column should be show */
  const showApprovalColumn = React.useMemo(() => {
    if (mode === "review") {
      return true;
    } else if (_.find(credits, c => c.status !== CreditRequestStatus.PendingApproval)) {
      return true;
    } else if (supportTicket?.creditStatus === CreditStatus.AutoReviewed || supportTicket?.creditStatus === CreditStatus.Reviewed) {
      return true;
    }

    return false;
  }, [
    credits,
    mode,
    supportTicket?.creditStatus
  ]);

  /** Boolean for if we should credit the whole order or require line by line products */
  const creditWholeOrder = React.useMemo(() => {
    if (supportTicket?.id && showApprovalColumn) {
      return false;
    }

    if (ticketType) {
      if (ticketType && typeof ticketType.options.allowCredits === "object" && ticketType.options.allowCredits.creditWholeOrder) {
        return true;
      }
    }

    return false;
  }, [
    showApprovalColumn,
    supportTicket?.id,
    ticketType
  ]);

  /** On fetch of the previous credits data load them into state */
  React.useEffect(() => {
    if (getCreditsByOrderRes.data) {
      setCreditState(currentState => ({
        ...currentState,
        isLoading: false,
        credits: getCreditsByOrderRes.data as ICreditRequest[]
      }));
    } else if (getCreditsByOrderRes.error) {
      toast.error(getCreditsByOrderRes.error.message);

      setCreditState(currentState => ({
        ...currentState,
        isLoading: false
      }));
    }
  }, [ getCreditsByOrderRes.data, getCreditsByOrderRes.error ]);

  const orderLinesWithCredits = React.useMemo(() => {
    if (!currentOrder.data) {
      return [];
    }

    return _.filter(currentOrder.data?.orderLines, ol => !!_.find(credits, cred => cred.orderLineId === ol.id));
  }, [ credits, currentOrder.data ]);

  const unselectedOrderLines = React.useMemo(() => {
    return _.filter(currentOrder.data?.orderLines, ol => !_.find(credits, cred => cred.orderLineId === ol.id));
  }, [ credits, currentOrder.data ]);

  React.useImperativeHandle<CreditRequestContextProviderRef, CreditRequestContextProviderRef>(ref, () => ({
    setSupportTicket(supportTicket: ISupportTicket | null) {
      if (!supportTicket) {
        setCreditState(currentState => ({
          ...currentState,
          isLoading: false
        }));
      } else if (supportTicket.orderId) {
        getCreditsByOrderReq({ orderId: supportTicket.orderId });

        setCreditState(currentState => ({
          ...currentState,
          supportTicket,
          mode: "view"
        }));
      }
    },
    setMode(mode: CreditRequestEditMode) {
      setCreditState(currentState => ({
        ...currentState,
        mode
      }));
    },
    setShowCloseTicketWithCreditsModal
  }));

  // use the persisted data
  React.useEffect(() => {
    if (!hasLoadedStorage) {
      // make sure the orders match before loading it
      if (currentOrder.data && currentOrder.data.id === props.supportTicket.orderId) {
        // if its an existing ticket
        if (props.supportTicket?.id) {
          // check if the credit ticket ids match from the persisted values
          if (creditStorage && creditStorage.ticketId === supportTicket?.id) {
            // use persisted values where possible
            setCreditState(currentState => ({
              ...currentState,
              isLoading: false,
              order: currentOrder.data,
              credits: creditStorage.mode === "view" ? props.supportTicket?.credits || [] : creditStorage.credits,
              mode: creditStorage.mode
            }));
            setHasLoadedStorage(true);
          } else {
            // do not use the persisted values
            setCreditState(currentState => ({
              ...currentState,
              mode: "view",
              isLoading: false,
              order: currentOrder.data,
              supportTicket: props.supportTicket,
              credits: props.supportTicket.credits || []
            }));
            setHasLoadedStorage(true);
          }
        } else {
          if (currentOrder.data.id) {
            getCreditsByOrderReq({ orderId: currentOrder.data.id });
          }

          // when its a new ticket so just load the order
          setCreditState(currentState => ({
            ...currentState,
            order: currentOrder.data
          }));
          setHasLoadedStorage(true);
        }
      }
    }
  }, [
    creditStorage,
    currentOrder,
    getCreditsByOrderReq,
    hasLoadedStorage,
    props.supportTicket,
    supportTicket
  ]);

  // keep the persisted data up to date
  React.useEffect(() => {
    if (hasLoadedStorage && supportTicket) {
      setCreditStorage({
        mode,
        credits,
        ticketId: supportTicket.id
      });
    }
  }, [
    credits,
    creditStorage.mode,
    hasLoadedStorage,
    supportTicket,
    setCreditStorage,
    mode
  ]);

  const setMode = React.useCallback((mode: CreditRequestEditMode) => {
    setCreditState(currentState => ({
      ...currentState,
      mode
    }));
  }, []);

  React.useEffect(() => {
    if (approveAll) {
      setCreditState(currentState => {
        return {
          ...currentState,
          hasChanged: true,
          credits: _.map(currentState.credits, credit => ({
            ...credit,
            quantityApproved: credit.supportTicketId === currentState.supportTicket?.id ? credit.quantityRequested : credit.quantityApproved
          }))
        };
      });
    }
  }, [ approveAll ]);

  /**
   * Add a new or ammend an existing credit row
   */
  const setCreditRequestRow = React.useCallback((credit: CreditRequestRowType, orderLine: IOrderLine) => {
    if (credit.localId || credit.id) {
      setCreditState(currentState => {
        const existingCreditIndex = _.findIndex(currentState.credits, cred => credit.localId ? cred.localId === credit.localId : cred.id === credit.id);
        const validatedCreditRow = validateCreditRow(currentState.credits, credit, orderLine, currentState.mode);

        currentState.credits.splice(existingCreditIndex, 1, validatedCreditRow);

        return {
          ...currentState,
          hasChanged: currentState.hasChanged === false && credit.error ? false : true,
          credits: [ ...currentState.credits ]
        };
      });
    } else {
      // add a new credit
      setCreditState(currentState => ({
        ...currentState,
        credits: _.concat(currentState.credits, [
          {
            ...credit,
            localId: uuid()
          }
        ])
      }));
    }
  }, []);

  /**
   * Saves the credits
   */
  const saveCredits = React.useCallback(() => {
    const formattedCredits = _.filter(credits, cred => !cred.supportTicketId || cred.supportTicketId === supportTicket?.id);

    const totalQuantityRequested = formattedCredits.reduce((total, credit) => {
      return total + credit.quantityRequested;
    }, 0);

    if (totalQuantityRequested === 0 && !creditWholeOrder) {
      toast.error("When requesting a credit against a product, quantity must be more than 0");

      return;
    }

    if (!formattedCredits.length && !creditWholeOrder) {
      toast.error("You must report at least one product issue to save this ticket");

      return;
    }

    const columns: Array<keyof CreditRequestRowType> = [
      "id",
      "creditRequestReasonCodeId",
      "orderLineId",
      "quantityRequested"
    ];

    if (mode === "review") {
      columns.push("quantityApproved");
    }

    // only require evidence on create of the ticket
    if (!supportTicket?.id && requireEvidence && !evidence.length) {
      toast.error("Evidence is required for this ticket type");
    } else {
      props.saveSupportTicket({
        credits: _.map(formattedCredits, c => _.pick(c, columns)),
        submitReview: mode === "review",
        evidence: evidence.map((file: FileData) => file.s3FileKey)
      });

      setCreditState(currentState => ({
        ...currentState,
        isLoading: true
      }));
    }
  }, [
    credits,
    creditWholeOrder,
    mode,
    requireEvidence,
    evidence,
    supportTicket?.id,
    props
  ]);

  /**
   * Cancel the credit request
   */
  const handleCancelCreditRequest = React.useCallback(() => {
    props.onCancelCreditRequest && props.onCancelCreditRequest();

    setCreditState(currentState => ({
      ...currentState,
      isLoading: true
    }));
  }, [ props ]);

  /**
   * Remove the selected credit row from the list
   */
  const removeCreditRow = React.useCallback((credit: CreditRequestRowType) => {
    setCreditState(currentState => {
      const existingCreditIndex = _.findIndex(currentState.credits, cred => credit.localId ? cred.localId === credit.localId : cred.id === credit.id);

      currentState.credits.splice(existingCreditIndex, 1);

      return {
        ...currentState,
        credits: [ ...currentState.credits ]
      };
    });
  }, []);

  const canAdjustCreditValues = React.useMemo(() => {
    if (!supportTicket) {
      return false;
    }

    if (supportTicket?.creditStatus === CreditStatus.Reviewed || supportTicket?.creditStatus === CreditStatus.AutoReviewed) {
      return false;
    }

    if (creditWholeOrder) {
      return false;
    }

    return true;
  }, [ creditWholeOrder, supportTicket ]);

  const canReviewCreditValues = React.useMemo(() => {
    if (currentRole?.type !== "admin") {
      return false;
    }

    if (!supportTicket) {
      return false;
    }

    if (hasExportedCreditForTicket) {
      return false;
    }

    return true;
  }, [
    currentRole?.type,
    hasExportedCreditForTicket,
    supportTicket
  ]);

  const canCancelCreditRequest = React.useMemo(() => {
    if (!supportTicket) {
      return false;
    }

    if (hasExportedCreditForTicket) {
      return false;
    }

    return true;
  }, [ hasExportedCreditForTicket, supportTicket ]);

  const showHeaders = React.useMemo(() => {
    return !!credits.length;
  }, [ credits ]);

  if (!supportTicket?.orderId) {
    // dont do anything if its not related to an order
    return null;
  }

  return (
    <CreditContextWrapper>
      <CreditRequestContext.Provider
        value={{
          state: {
            supportTicket,
            credits,
            mode,
            isLoading,
            order,
            hasChanged,
            approveAll
          },
          actions: {
            setCreditRequestRow,
            saveCredits,
            removeCreditRow
          }
        }}
      >

        {/** if supportTicket.id is 0, we're in the process of creating ticket. */}
        {creditWholeOrder && supportTicket.id === 0 ? (
          <div>
            <Text>
              System will automatically report issues for all product lines
            </Text>

            <CreditRequestStatusBar
              creditWholeOrder
              canAmendQuantities={false}
            />
          </div>
        ) : (
          <CreditRequestSection>

            <Grid.Bounds className="issues-header">
              <Text fontSize="h2">
                What products were affected?
              </Text>

              {mode === "view" && (
                <div className="button-actions">
                  {canAdjustCreditValues && (
                    <Button onClick={() => setMode("edit")}>
                      Adjust values
                    </Button>
                  )}

                  {canReviewCreditValues && (
                    <Button onClick={() => setMode("review")}>
                      {supportTicket?.creditStatus === CreditStatus.Reviewed || supportTicket?.creditStatus === CreditStatus.AutoReviewed ? "Amend Approved" : "Review"}
                    </Button>
                  )}

                  {canCancelCreditRequest && (
                    <Button
                      onClick={() => {
                        // if there are approved credits, show model informing the user these will be zeroed out, asking them to confirm to proceed
                        if (credits.length && supportTicket?.creditStatus === CreditStatus.Reviewed || supportTicket?.creditStatus === CreditStatus.AutoReviewed) {
                          setShowCloseTicketWithCreditsModal(true);
                        } else {
                          handleCancelCreditRequest();
                        }
                      }}
                    >
                      Cancel Credit Request
                    </Button>
                  )}
                </div>
              )}

              {mode === "review" && (
                <div className="approve-all">
                  <Text>
                    Approve all
                  </Text>

                  <CheckboxStyled
                    name="approve-all"
                    id="approve-all"
                    checked={approveAll}
                    onChange={() => setCreditState(currentState => ({
                      ...currentState,
                      approveAll: !currentState.approveAll
                    }))}
                  />
                </div>
              )}
            </Grid.Bounds>

            {isLoading ? (
              <LoadingSpinner
                type="circular"
                position="inline"
              />
            ) :
              (
                <>
                  {showHeaders && (
                    <Grid.Bounds direction="vertical">

                      <Grid.Bounds className="credit-row credit-header">
                        <Grid.Bounds className="row-left">
                          <div className="credit-col heading-col product-col">
                            <Text weight="bold">
                              Product
                            </Text>
                          </div>

                          <div className="credit-col heading-col price-col">
                            <Text weight="bold">
                              Price
                            </Text>
                          </div>

                          <div className="credit-col heading-col allowed-quantity-col">
                            <Text weight="bold">
                              QTY
                            </Text>
                          </div>
                        </Grid.Bounds>

                        <CreditRowHeadersRight showApprovalColumn={showApprovalColumn} />
                      </Grid.Bounds>

                    </Grid.Bounds>
                  )}

                  {_.map(orderLinesWithCredits, ol => (
                    <CreditRequestOrderLine
                      showApprovalColumn={showApprovalColumn}
                      key={`ol-${ol.id}`}
                      orderLine={ol}
                    />
                  ))}

                  {(!supportTicket.id || [ "edit" ].includes(mode)) && (
                    <CreditOrderLineSelection
                      showApprovalColumn={showApprovalColumn}
                      unselectedOrderLines={unselectedOrderLines}
                    />
                  )}

                  {mode === "edit" && (
                    <>
                      {requireEvidence && (
                        <ErrorNotice
                          type="warn"
                          message="Evidence must be supplied for this credit request to be approved"
                        />
                      )}

                      <div className="dropzone-wrapper">
                        <DropZoneMultiFile
                          updateFilesToSave={setEvidence}
                          dropzoneTitle="Evidence"
                          dropzoneMessage="Drag and drop files into this box you would like to pass as evidence."
                        />
                      </div>
                    </>
                  )}

                  <CreditRequestStatusBar canAmendQuantities />
                </>
              )}
          </CreditRequestSection>
        )}
      </CreditRequestContext.Provider>

      <CloseTicketWithCreditsModel
        open={showCloseTicketWithCreditsModal}
        onDismiss={() => setShowCloseTicketWithCreditsModal(false)}
        onConfirm={() => {
          handleCancelCreditRequest();
          setShowCloseTicketWithCreditsModal(false);
        }}
      />
    </CreditContextWrapper>
  );
};

export default React.forwardRef(CreditRequestContextProvider);

const CreditContextWrapper = styled.div`
  ${({ theme }) => css`
    order: 3;
    width: 100%;
    margin-top: ${theme.spacing.margins.lg};
  `}
`;

const CreditRequestSection = styled.div`
${({ theme }) => css`
  padding: ${theme.spacing.padding.md} ${theme.spacing.padding.lg};
  background: ${theme.colors.lightestGrey};
  
  .issues-header {
    justify-content: space-between;
  }

  .button-actions {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    > div {
      margin-bottom: ${theme.spacing.margins.sm};
    }
  }

  .approve-all {
    justify-self: flex-end;
  }

  .credit-row {
    width: 100%;
    flex-wrap: wrap;
    overflow-x: scroll;

    .row-right {
      overflow-x: ;
    }

    &.credit-header {
      .row-right { 
        display: none;
      }
    }

    .row-right {
      width: 100%;
      justify-content: space-between;
    }

    .row-left {
      width: 100%;
      justify-content: space-between;
      background: ${theme.colors.white};
    }

    ${breakpoint("md", theme)} {
      flex-wrap: no-wrap;
      
      &.credit-header {
        .row-right { 
          display: flex;
        }
      }

      .row-right {
        width: 40%;
      }
  
      .row-left {
        width: 60%;
      }
    }
  }

  ${breakpoint("sm", theme)} {
    .button-actions {
      width: auto;
      display: flex;
      flex-direction: row;
  
      > div {
        margin-left: ${theme.spacing.margins.sm};
        margin-bottom: 0;
      }
    }
  }


  .credit-header {
    .row-left {
      background: ${theme.colors.lightestGrey};
    }
  }

  .credit-col {
    padding: ${theme.spacing.padding.md};

    &.heading-col.product-col {
      padding-left: 0;
    }

    &.product-col {
      width: 200px;
    }

    &.price-col {
      width: 100px;
    }

    &.reason-col {
      width: 200px;
    }

    &.allowed-quantity-col {
      width: 70px;
    }

    &.requested-quantity-col {
      width: 90px;
    }

    &.approved-quantity-col {
      width: 90px;
    }
  }
  
  .dropzone-wrapper {
    padding: ${theme.spacing.padding.md};
    border-bottom: 1px solid ${theme.colors.lightGrey};
  }
`}
`;
